/**
 * VideoCarousel Widget
 *
 * Displays a carousel of videos that the user must watch.
 * Tracks watched videos via YouTube IFrame API (real playback state) and calls onComplete when all are viewed.
 * Auto-hides once all videos have been watched (matching Flutter SizedBox.shrink() behaviour).
 */

import { cn } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tonnie/ui';
import { CheckCircle2, ChevronLeft, ChevronRight, Play, X } from '@tonnie/assets';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WidgetProps } from '@tonnie/copilot';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';
import { TYPE_SCALE } from '@tonnie/design-tokens';

// ═══════════════════════════════════════════════════════════════════════
// YOUTUBE IFRAME API LOADER (singleton)
// ═══════════════════════════════════════════════════════════════════════

/** Global YT types — declared inline to avoid adding @types/youtube dependency */
declare global {
  interface Window {
    YT?: {
      Player: new (
        elementId: string | HTMLElement,
        config: {
          videoId: string;
          playerVars?: Record<string, unknown>;
          events?: Record<string, (event: YTEvent) => void>;
        }
      ) => YTPlayer;
      PlayerState: {
        UNSTARTED: -1;
        ENDED: 0;
        PLAYING: 1;
        PAUSED: 2;
        BUFFERING: 3;
        CUED: 5;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTEvent {
  data: number;
  target: YTPlayer;
}

interface YTPlayer {
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  destroy: () => void;
}

let ytApiPromise: Promise<void> | null = null;

function loadYouTubeIframeAPI(): Promise<void> {
  if (ytApiPromise) return ytApiPromise;
  if (window.YT?.Player) {
    ytApiPromise = Promise.resolve();
    return ytApiPromise;
  }
  ytApiPromise = new Promise<void>((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });
  return ytApiPromise;
}

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface IVideo {
  id?: string;
  videoId?: string;
  code?: number;
  title: string;
  description?: string;
  imageUrl?: string;
  duration?: string;
}

interface IVideoCarouselConfig {
  videos?: IVideo[];
  requireAllWatched?: boolean;
  /**
   * Fraction of a video (0–1) that counts as "watched". Playing past this
   * point marks the video complete without needing to reach the end — a
   * soft control so a user who stops at ~90–95% isn't left feeling the
   * video "didn't mark". Defaults to 0.75. The YouTube `ENDED` event still
   * marks completion as a fallback (short videos / full watch-throughs).
   */
  watchThreshold?: number;
}

/** Default soft-completion threshold when the config omits `watchThreshold`. */
const DEFAULT_WATCH_THRESHOLD = 0.75;

// ═══════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════

const getVideoId = (video: IVideo, index?: number): string =>
  video.videoId || video.id || (video.code ? String(video.code) : '') || `video_${index ?? 0}`;

const formatDuration = (duration?: string): string => {
  if (!duration) return '0:00';
  return duration;
};

/** Generate a YouTube thumbnail URL with fallback chain. */
const getYouTubeThumbnail = (videoId: string): string | null =>
  videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;

const getYouTubeFallback = (videoId: string): string | null =>
  videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

// ═══════════════════════════════════════════════════════════════════════
// HOOK — useWindowWidth
// ═══════════════════════════════════════════════════════════════════════

function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export function VideoCarouselWidget({
  config,
  onComplete,
}: WidgetProps & { config?: IVideoCarouselConfig }) {
  const { t } = useTranslation();
  const windowWidth = useWindowWidth();
  const isSmallScreen = windowWidth < 768;

  const videoList: IVideo[] = config?.videos ?? [];
  const requireAll = config?.requireAllWatched !== false;
  const watchThreshold = config?.watchThreshold ?? DEFAULT_WATCH_THRESHOLD;

  // Persistent slice — survives tab close, refresh, cross-device login.
  // Stored under the key `video_carousel` with no `keyScope`: the hero
  // renders this widget once, so its slice has no twin to collide with. Replaces what was a non-persistent `useState<Set>`
  // pre-2026-04-25 — users who watched 2 of 5 videos and left would
  // have to re-watch all 5 on return. Now they don't.
  // `progress` maps videoId → max fraction watched (0–1). It powers the
  // per-card "in-progress" / "resume" affordance so a partially-watched
  // video is visible on the carousel, not just a binary watched/unwatched.
  // Backward-compatible: rows persisted before this field simply lack it.
  const [persistedState, patchPersisted] = useWidgetState<{
    watched: string[];
    currentIndex: number;
    progress?: Record<string, number>;
  }>('video_carousel', { watched: [], currentIndex: 0, progress: {} });

  // Local Set view of `watched` for O(1) membership checks during render.
  const watchedVideos = useMemo(() => new Set(persistedState.watched), [persistedState.watched]);
  const progressMap = persistedState.progress ?? {};
  const currentIndex = persistedState.currentIndex;

  const [dialogVideoId, setDialogVideoId] = useState<string | null>(null);
  const [dialogTitle, setDialogTitle] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle all-watched trigger. Deferred while a video dialog is open: the
  // soft 75% threshold can mark the LAST video mid-playback, and firing
  // completion then would yank the widget (and the open player) away, cutting
  // off the final ~25%. We wait until the user closes the dialog — at which
  // point `dialogVideoId` is null and this re-evaluates — before advancing.
  useEffect(() => {
    if (
      requireAll &&
      watchedVideos.size >= videoList.length &&
      videoList.length > 0 &&
      dialogVideoId === null
    ) {
      const timer = setTimeout(() => onComplete?.(), 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [watchedVideos, videoList.length, requireAll, onComplete, dialogVideoId]);

  const markWatched = useCallback(
    (videoId: string) => {
      // Append the videoId to the persisted watched list (dedup via Set).
      // The patch is queued + debounced inside useWidgetState; we don't
      // await — the local state has already updated optimistically.
      const next = Array.from(new Set([...persistedState.watched, videoId]));
      patchPersisted({ watched: next });
    },
    [persistedState.watched, patchPersisted]
  );

  // Record the furthest fraction (0–1) a video has been watched to. Never
  // regresses (a re-watch that stops early keeps the higher mark). The
  // updater form reads the LATEST persisted map so rapid closes don't clobber
  // one another. The dialog flushes this once on close (not per poll tick),
  // so writes stay cheap.
  const setProgress = useCallback(
    (videoId: string, ratio: number) => {
      const clamped = Math.max(0, Math.min(1, ratio));
      patchPersisted((prev) => {
        const prevProgress = prev.progress ?? {};
        if (clamped <= (prevProgress[videoId] ?? 0)) return {};
        return { progress: { ...prevProgress, [videoId]: clamped } };
      });
    },
    [patchPersisted]
  );

  // Programmatic scroll to index (matching mockup)
  const CARD_WIDTH = 307;
  const CARD_GAP = 16;
  const PADDING = 24;

  const scrollToIndex = useCallback(
    (index: number) => {
      if (!scrollContainerRef.current || index < 0 || index >= videoList.length) return;
      const scrollPosition =
        index * (CARD_WIDTH + CARD_GAP) - (windowWidth / 2 - CARD_WIDTH / 2) + PADDING;
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth',
      });
      patchPersisted({ currentIndex: index });
    },
    [videoList.length, windowWidth, patchPersisted]
  );

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const scrollOffset = scrollContainerRef.current.scrollLeft;
    const newIndex = Math.round((scrollOffset - PADDING) / (CARD_WIDTH + CARD_GAP));
    if (newIndex >= 0 && newIndex < videoList.length && newIndex !== currentIndex) {
      patchPersisted({ currentIndex: newIndex });
    }
  }, [videoList.length, currentIndex, patchPersisted]);

  const openVideoDialog = (video: IVideo, index: number) => {
    const id = getVideoId(video, index);
    setDialogVideoId(id);
    setDialogTitle(video.title);
  };

  const closeVideoDialog = useCallback(() => {
    setDialogVideoId(null);
    setDialogTitle('');
  }, []);

  // ─── Empty state ───
  if (videoList.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">{t('copilot.noVideos')}</div>
    );
  }

  // ─── Auto-hide when all watched ───
  // Keep the widget mounted while a video dialog is open, even if that last
  // video just crossed the threshold — returning null here would unmount the
  // open <VideoPlayerDialog> and close the video mid-watch. The user finishes
  // and closes the player themselves; the widget then hides + completes.
  if (requireAll && watchedVideos.size >= videoList.length && dialogVideoId === null) {
    return null;
  }

  return (
    <>
      <div className="py-6" data-testid="widget-video_carousel">
        <div className="relative">
          {/* Video Cards */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className={cn(
              'flex overflow-x-auto gap-4 scroll-smooth scrollbar-hide snap-x snap-mandatory',
              isSmallScreen ? 'px-2' : 'px-6'
            )}
          >
            {videoList.map((video, index) => {
              const id = getVideoId(video, index);
              const isWatched = watchedVideos.has(id);
              const videoProgress = isWatched ? 1 : (progressMap[id] ?? 0);
              return (
                <VideoCard
                  key={id}
                  video={video}
                  index={index}
                  isWatched={isWatched}
                  progress={videoProgress}
                  onTap={() => openVideoDialog(video, index)}
                />
              );
            })}
          </div>

          {/* Navigation Arrows — hidden on mobile */}
          {!isSmallScreen && videoList.length > 1 && (
            <>
              {currentIndex > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => scrollToIndex(currentIndex - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/70 hover:bg-black/80 text-white z-10"
                  aria-label={t('copilot.previousVideo')}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              )}
              {currentIndex < videoList.length - 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => scrollToIndex(currentIndex + 1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/70 hover:bg-black/80 text-white z-10"
                  data-testid="widget-video_carousel-next"
                  aria-label={t('copilot.nextVideo')}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              )}
            </>
          )}
        </div>

        {/* Progress counter */}
        <div
          className={cn(
            'flex items-center gap-2 text-xs mt-3 px-6 transition-colors',
            watchedVideos.size >= videoList.length
              ? 'text-success-accessible font-semibold'
              : 'text-muted-foreground'
          )}
          data-testid="widget-video_carousel-complete"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          <span>
            {watchedVideos.size}/{videoList.length} {t('copilot.videosWatched')}
          </span>
        </div>
      </div>

      {/* Video Player Dialog */}
      {dialogVideoId && (
        <VideoPlayerDialog
          videoId={dialogVideoId}
          title={dialogTitle}
          watchThreshold={watchThreshold}
          onClose={closeVideoDialog}
          onWatched={() => markWatched(dialogVideoId)}
          onProgress={(ratio) => setProgress(dialogVideoId, ratio)}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VideoCard — dark branded card with YouTube thumbnail
// ═══════════════════════════════════════════════════════════════════════

interface VideoCardProps {
  video: IVideo;
  index: number;
  isWatched: boolean;
  /** Furthest fraction (0–1) watched — drives the in-progress / resume state. */
  progress: number;
  onTap: () => void;
}

/**
 * A thumbnail card with three clearly-distinct states so a user can tell at a
 * glance what's done vs. what's left (icon + text + colour — never colour
 * alone, for a11y & colour-blind users):
 *   • pending      → neutral "To watch" pill + centred play affordance
 *   • in-progress  → brand "Resume" pill + bottom progress bar
 *   • watched      → green "Watched" pill + green ring around the card
 */
function VideoCard({ video, index, isWatched, progress, onTap }: VideoCardProps) {
  const { t } = useTranslation();
  const videoId = video.videoId || video.id || '';
  const thumbnailUrl = video.imageUrl || getYouTubeThumbnail(videoId);
  const fallbackUrl = getYouTubeFallback(videoId);

  const isInProgress = !isWatched && progress > 0;
  const progressPct = Math.round(Math.max(0, Math.min(1, progress)) * 100);
  const state = isWatched ? 'watched' : isInProgress ? 'in_progress' : 'pending';
  const stateLabel = isWatched
    ? t('copilot.videoStateWatched')
    : isInProgress
      ? t('copilot.videoStateInProgress')
      : t('copilot.videoStatePending');

  return (
    <div
      onClick={onTap}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onTap();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${video.title} — ${stateLabel}`}
      data-testid={`widget-video_carousel-item-${index}`}
      data-video-state={state}
      className={cn(
        'group shrink-0 bg-primary rounded-lg shadow-md cursor-pointer overflow-hidden w-[307px] snap-start transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isWatched ? 'ring-2 ring-success' : 'ring-1 ring-transparent hover:ring-white/20'
      )}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden h-[173px]">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              if (fallbackUrl && e.currentTarget.src !== fallbackUrl) {
                e.currentTarget.src = fallbackUrl;
              } else {
                e.currentTarget.style.display = 'none';
                const next = e.currentTarget.nextElementSibling as HTMLElement;
                if (next) next.style.display = 'flex';
              }
            }}
          />
        ) : null}

        {/* Placeholder fallback */}
        <div
          className="w-full h-full bg-primary/80 flex items-center justify-center text-white"
          style={{ display: thumbnailUrl ? 'none' : 'flex', fontSize: TYPE_SCALE['5xl'] }}
        >
          <Play className="w-12 h-12 text-white/60" />
        </div>

        {/* Scrim — keeps overlays legible over any thumbnail */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10 pointer-events-none" />

        {/* Centred play / resume affordance — clear "tap to watch" cue (hidden once watched) */}
        {!isWatched && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm ring-1 ring-white/40 shadow-lg flex items-center justify-center transition-transform group-hover:scale-110">
              <Play className="w-7 h-7 text-white fill-white ml-0.5" />
            </div>
          </div>
        )}

        {/* Duration Badge — top-left overlay */}
        {video.duration && (
          <div className="absolute top-2.5 left-2 bg-black/50 rounded px-2 py-1 text-xs font-bold text-white">
            {formatDuration(video.duration)}
          </div>
        )}

        {/* Status pill — top-right (icon + text + colour) */}
        <div className="absolute top-2 right-2">
          {isWatched ? (
            <span className="flex items-center gap-1 bg-success text-white rounded-full pl-1.5 pr-2.5 py-1 text-xs font-semibold shadow-md">
              <CheckCircle2 className="w-4 h-4" />
              {t('copilot.videoWatchedLabel')}
            </span>
          ) : isInProgress ? (
            <span className="flex items-center gap-1 bg-primary text-white rounded-full pl-1.5 pr-2.5 py-1 text-xs font-semibold shadow-md ring-1 ring-white/30">
              <Play className="w-3.5 h-3.5 fill-white" />
              {t('copilot.videoResume')}
            </span>
          ) : (
            <span className="flex items-center gap-1 bg-black/60 text-white/90 rounded-full pl-1.5 pr-2.5 py-1 text-xs font-semibold shadow-md backdrop-blur-sm">
              <Play className="w-3.5 h-3.5 fill-white/90" />
              {t('copilot.videoToWatch')}
            </span>
          )}
        </div>

        {/* In-progress bar — bottom of the thumbnail. Decorative: the card
            root's aria-label already announces the "In progress" state, so
            this reinforcement is hidden from screen readers. */}
        {isInProgress && (
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/40" aria-hidden="true">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        )}
      </div>

      {/* Title */}
      <div className="p-3">
        <div className="text-sm font-bold text-white text-center line-clamp-2">{video.title}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VideoPlayerDialog — YouTube IFrame API with real completion tracking
// ═══════════════════════════════════════════════════════════════════════

interface VideoPlayerDialogProps {
  videoId: string;
  title: string;
  /** Fraction (0–1) of playback that marks the video watched. */
  watchThreshold: number;
  onClose: () => void;
  onWatched: () => void;
  /** Reports the furthest fraction (0–1) watched; flushed once on close. */
  onProgress: (ratio: number) => void;
}

function VideoPlayerDialog({
  videoId,
  title,
  watchThreshold,
  onClose,
  onWatched,
  onProgress,
}: VideoPlayerDialogProps) {
  const { t } = useTranslation();
  const windowWidth = useWindowWidth();

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasWatched, setHasWatched] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [apiReady, setApiReady] = useState(false);

  const playerRef = useRef<YTPlayer | null>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasCalledWatched = useRef(false);
  // Furthest fraction watched this session, flushed to the parent once on
  // unmount (cheap — no per-tick network writes). `onProgress` is held in a
  // ref so the unmount effect can stay dependency-free and not re-run.
  const latestRatioRef = useRef(0);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  useEffect(
    () => () => {
      if (latestRatioRef.current > 0) onProgressRef.current(latestRatioRef.current);
    },
    []
  );

  // Load YouTube IFrame API on mount
  useEffect(() => {
    loadYouTubeIframeAPI().then(() => setApiReady(true));
  }, []);

  // Initialise YT.Player once the user clicks play and API is ready
  useEffect(() => {
    if (!isPlaying || !apiReady || !window.YT || !playerContainerRef.current) return;

    // Unique DOM id for the player element
    const containerId = `yt-player-${videoId}`;
    playerContainerRef.current.id = containerId;

    // Mark the video watched exactly once — fired by the soft 75% threshold
    // OR the YouTube ENDED event, whichever comes first. Idempotent via
    // `hasCalledWatched`, so the second path is a safe no-op.
    const markComplete = () => {
      if (hasCalledWatched.current) return;
      hasCalledWatched.current = true;
      setHasWatched(true);
      setVideoProgress(100);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      onWatched();
    };

    const player = new window.YT.Player(containerId, {
      videoId,
      playerVars: {
        autoplay: 1,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
      },
      events: {
        onStateChange: (event: YTEvent) => {
          // YT.PlayerState.ENDED === 0 — full watch-through. Fallback path:
          // the 75% threshold below usually marks the video first.
          if (event.data === 0) {
            latestRatioRef.current = 1;
            markComplete();
          }
        },
        onReady: () => {
          // Poll real playback every 500ms: drives the progress bar, records
          // the furthest fraction watched, and soft-completes at the
          // threshold so a user who stops at ~90–95% still gets marked.
          progressIntervalRef.current = setInterval(() => {
            if (!playerRef.current) return;
            try {
              const current = playerRef.current.getCurrentTime();
              const duration = playerRef.current.getDuration();
              if (duration <= 0) return;
              const ratio = current / duration;
              latestRatioRef.current = Math.max(latestRatioRef.current, ratio);
              setVideoProgress(Math.min(100, ratio * 100));
              if (ratio >= watchThreshold) markComplete();
            } catch {
              // Player may have been destroyed
            }
          }, 500);
        },
      },
    });

    playerRef.current = player;

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      try {
        playerRef.current?.destroy();
      } catch {
        // Player may already be destroyed
      }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, apiReady, videoId]);

  const handlePlay = () => setIsPlaying(true);

  // Responsive dialog sizing: 16:9 ratio
  const isSmallScreen = windowWidth < 768;
  const dialogWidth = isSmallScreen ? windowWidth * 0.95 : Math.min(windowWidth * 0.8, 1200);
  const dialogHeight = dialogWidth * (9 / 16);

  // Human-readable progress (e.g. "2:30 / 5:00")
  const progressLabel = useMemo(() => {
    if (!playerRef.current || !isPlaying) return null;
    try {
      const current = playerRef.current.getCurrentTime?.() ?? 0;
      const duration = playerRef.current.getDuration?.() ?? 0;
      if (duration <= 0) return null;
      const fmt = (s: number) => {
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, '0')}`;
      };
      return `${fmt(current)} / ${fmt(duration)}`;
    } catch {
      return null;
    }
  }, [videoProgress, isPlaying]); // videoProgress triggers recalc

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        className="p-0 bg-primary rounded-xl overflow-hidden border-0 !max-w-none"
        style={{
          width: `${dialogWidth}px`,
          height: `${dialogHeight}px`,
          maxWidth: '95vw',
          maxHeight: '95vh',
        }}
      >
        {/* Accessibility — sr-only */}
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('copilot.videoPlayerDesc', { title })}</DialogDescription>
        </DialogHeader>

        {/* Video area */}
        <div className="w-full h-full bg-black relative">
          {!isPlaying ? (
            /* Pre-play state: title + big red play button */
            <div className="w-full h-full flex flex-col items-center justify-center p-8">
              <div className="text-3xl font-bold text-white text-center mb-8">{title}</div>
              <Button
                onClick={handlePlay}
                className="w-20 h-20 rounded-full bg-destructive hover:bg-destructive/90 flex items-center justify-center cursor-pointer"
                data-testid="widget-video_carousel-play"
                aria-label={t('copilot.playVideo')}
              >
                <Play className="w-10 h-10 text-white fill-white ml-1" aria-hidden="true" />
              </Button>
              <div className="mt-6 text-sm text-white/90 text-center">
                {t('copilot.clickToPlay')}
              </div>
            </div>
          ) : (
            /* YouTube IFrame API player container */
            <>
              <div ref={playerContainerRef} className="w-full h-full" />
              {/* Real progress bar — tracks actual YouTube playback */}
              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/50 z-[5]">
                <div
                  className={cn(
                    'h-full transition-all duration-500 ease-out',
                    hasWatched ? 'bg-success' : 'bg-destructive'
                  )}
                  style={{ width: `${videoProgress}%` }}
                />
              </div>
              {/* Time label */}
              {progressLabel && !hasWatched && (
                <div className="absolute bottom-3 left-3 z-[5] bg-black/70 rounded px-2 py-0.5 text-xs text-white/90 font-mono">
                  {progressLabel}
                </div>
              )}
            </>
          )}
        </div>

        {/* Watched checkmark + label */}
        {hasWatched && (
          <div className="absolute top-4 right-14 z-10 flex items-center gap-2">
            <span className="text-xs font-semibold text-white bg-success/90 rounded-full px-2.5 py-1 shadow-lg">
              {t('copilot.videoCompleted')}
            </span>
            <div className="w-10 h-10 rounded-full bg-success flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
          </div>
        )}

        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background border border-border/50 shadow-sm"
          data-testid="widget-video_carousel-skip"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </Button>
      </DialogContent>
    </Dialog>
  );
}
